import { useState } from 'react';
import { v4 } from 'uuid';
import { useRecoilValue } from 'recoil';
import { MetricsTypeEnum } from '~/backend/types';
import { StructureSet } from '~/domain/patient/structureset';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { pseudonymizeState } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import useAnonymizer from '../useAnonymizer';
import { useMetricsBackend, useSessionID } from '../useBackend';
import { useUploadAndProcessMetrics } from '../useUploadAndProcessMetrics';

export type SegmentationData = {
    sdice1: number;
    sdice3: number;
    sdice5: number;
    average_apl_mm: number | null;
    total_apl_mm: number | null;
    hd: number | null;
    mean_hd: number | null;
    user_z_length_mm: number | null;
    gt_z_length_mm: number | null;
    dice: number;
    sdice2: number;
    hd95: number;
    user_vol_mm3: number;
    gt_vol_mm3: number;
    gt_roi_name: string;
    user_roi_name: string;
};

/**
 * A React hook for performing segmetrics caculation based on selected DICOM data.
 *
 * @param {Object} callbacks - An object containing callback functions for success and error handling.
 * - @param {Function} callbacks.onSubmit - A callback function to be executed upon successful submission of files for processing.
 *
 * @returns {Function} submit - A function that triggers the segmetrics process.
 * @returns {Function} loading - Indicates if function is running still.
 */
export function useSegmentationMetrics() {
    const client = useMetricsBackend();
    const { anonymizeBlobs } = useAnonymizer();
    const primaryImage = useRecoilValue(primaryImageState);
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const { resetSessionID, sessionID } = useSessionID();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<SegmentationData[]>();

    const handleProcessedFiles = async (files: File[]) => {
        setLoading(false);
        if (files) {
            const metrics = await Promise.all(
                files.map(async (file) => {
                    const fileArrayBuffer = await file.arrayBuffer();
                    return JSON.parse(
                        new TextDecoder().decode(fileArrayBuffer as ArrayBuffer)
                    );
                })
            );
            setData(metrics[0]);
        }
    };

    const upload = useUploadAndProcessMetrics(
        MetricsTypeEnum.SEGMETRICS,
        client,
        {
            onSuccess: (files) => {
                handleProcessedFiles(files);
            },
        }
    );

    const submit = async (
        testStructureSet: StructureSet,
        referenceStructureSet: StructureSet
    ) => {
        try {
            setLoading(true);
            resetSessionID();
            const taskUID = `${sessionID}.${v4().split('-')[0]}`;
            const blobs = [
                {
                    file: referenceStructureSet.dicomStructureSet.getAsDicomBlob(),
                    filename: 'ground_truth',
                },
                {
                    file: testStructureSet.dicomStructureSet.getAsDicomBlob(),
                    filename: 'user',
                },
            ];

            const blobsToUpload = pseudonymize
                ? await Promise.all(
                      blobs.map(async (blob) => {
                          const result = await anonymizeBlobs(taskUID, [
                              blob.file,
                          ]);
                          return {
                              file: result![0],
                              filename: blob.filename,
                          };
                      })
                  )
                : blobs;

            if (blobsToUpload && primaryImage) {
                upload(blobsToUpload, 'ground_truth', 'user', {
                    origin: [
                        ...primaryImage!.grid.getCornerVoxelCenterPosition(),
                    ],
                    direction: [
                        [...primaryImage!.grid.getUnitX()],
                        [...primaryImage!.grid.getUnitY()],
                        [...primaryImage!.grid.getUnitZ()],
                    ],
                    spacing: [...primaryImage!.grid.getRes()],
                    size: [...primaryImage!.grid.getSize()],
                });
            } else {
                handleError(
                    undefined,
                    'Segmentation metrics failed for one or more files.'
                );
                setLoading(false);
            }
        } catch (error) {
            handleError(
                'An error occurred during segmentation metrics processing.'
            );
            setLoading(false);
        }
    };

    return { submit, loading, data };
}
